import { Check } from 'lucide-react';
import { ProTag } from '@/components/ui/pro-tag';
import type { PdfTemplateName } from '@/types/database';
import { useFeature } from '@/hooks/useFeature';
import {
  PdfTemplatePreview,
  PDF_TEMPLATE_LABELS,
  PDF_TEMPLATE_DESCRIPTIONS,
} from './PdfTemplatePreview';

interface PdfTemplatePickerProps {
  value: PdfTemplateName;
  onChange: (template: PdfTemplateName) => void;
  /** Cor da marca da empresa, usada nos previews. */
  accent?: string;
  disabled?: boolean;
}

/**
 * Grade de escolha de modelo de PDF (preview + rótulo + gate PRO), usada em
 * TRÊS lugares: configurações da empresa (Modal e Page, onde define o PADRÃO
 * dos novos orçamentos) e o card "Modelo do PDF" do formulário de orçamento
 * (onde sobrepõe o padrão para um documento). O markup vivia duplicado nos dois
 * primeiros; a terceira cópia foi o empurrão para extrair.
 *
 * Gate PRO: grátis só usa `classico` — clicar num modelo travado dispara o
 * upsell (`require()`), igual ao comportamento histórico das configurações.
 */
export function PdfTemplatePicker({
  value,
  onChange,
  accent = '#10B981',
  disabled,
}: PdfTemplatePickerProps) {
  const pdfTemplates = useFeature('pdf_templates');

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {(Object.keys(PDF_TEMPLATE_LABELS) as PdfTemplateName[]).map((tpl) => {
          const isSelected = value === tpl;
          const isLocked = !pdfTemplates.allowed && tpl !== 'classico';
          return (
            <button
              key={tpl}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (isLocked) {
                  pdfTemplates.require();
                  return;
                }
                onChange(tpl);
              }}
              className={`relative flex flex-col items-center gap-1 rounded-lg border p-2 text-left transition ${
                isSelected
                  ? 'border-primary ring-2 ring-primary/40 bg-primary/5'
                  : 'border-input hover:border-primary/50'
              } ${isLocked ? 'opacity-70' : ''}`}
              aria-pressed={isSelected}
              aria-label={`Modelo ${PDF_TEMPLATE_LABELS[tpl]}`}
            >
              <PdfTemplatePreview template={tpl} accent={accent} className="h-24 w-full rounded" />
              <span className="text-[11px] font-medium text-foreground truncate w-full text-center">
                {PDF_TEMPLATE_LABELS[tpl]}
              </span>
              {isLocked && <ProTag className="absolute right-1 top-1" />}
              {isSelected && !isLocked && (
                <span className="absolute top-1 left-1 rounded-full bg-primary p-0.5 text-white">
                  <Check className="h-3 w-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{PDF_TEMPLATE_DESCRIPTIONS[value]}</p>
    </div>
  );
}
